import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Alert } from 'src/app/models/alert';
import { AlertDetailService } from 'src/app/diagnostics/component/alert-detail/alert-detail.service';
import { AlertSummary } from '../models/service-center-asset-detail';
import type { AlertDetailDialogData } from './alert-detail-dialog.component';

export const ALERT_QUERY_PARAM = 'alert';

export function alertIdFromLocation(): string {
  return new URL(window.location.href).searchParams.get(ALERT_QUERY_PARAM)?.trim() ?? '';
}

// Built from the full href so camera params the map stamps via raw replaceState survive.
export function hrefWithAlert(href: string, alertId: string | null): string {
  const url = new URL(href);
  if (alertId) url.searchParams.set(ALERT_QUERY_PARAM, alertId);
  else url.searchParams.delete(ALERT_QUERY_PARAM);
  return url.pathname + url.search + url.hash;
}

export function removeAlertParam(router: Router): void {
  void router.navigateByUrl(hrefWithAlert(window.location.href, null), { replaceUrl: true });
}

// GET alert/{id} answers a missing or out-of-scope alert with a 200 null-sentinel DTO, not a 404.
const EMPTY_OBJECT_ID = '000000000000000000000000';

export function isMissingAlert(alert: Alert | null | undefined, requestedId: string): boolean {
  return !alert?.id || alert.id === EMPTY_OBJECT_ID || alert.id !== requestedId;
}

export function fetchLinkedAlert(service: AlertDetailService, id: string): Observable<Alert | null> {
  return service.getData(id).pipe(
    map(alert => (isMissingAlert(alert, id) ? null : alert)),
    catchError(() => of(null)),
  );
}

export function alertToSummary(alert: Alert): AlertSummary {
  return {
    alertRuleId: alert.alertRule?.id,
    latestAlertId: alert.id,
    subject: alert.alertRule?.subject,
    severity: alert.severity?.name,
    count: alert.count ?? 1,
    lastFiredAtUtc: alert.createDateTimeUtc ? new Date(alert.createDateTimeUtc).toISOString() : '',
    vehicleName: alert.vehicleName,
    vin: alert.vehicleState?.vin,
  };
}

export function linkedAlertData(alert: Alert): AlertDetailDialogData {
  return { alert: alertToSummary(alert), vehicleName: alert.vehicleName, vin: alert.vehicleState?.vin };
}
